import React from 'react'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { message } from 'antd'
import * as Constants from '../constants'
import Token from '../token'
import ErrorNotification from '../components/ErrorNotification/ErrorNotification'

message.config({ prefixCls: 'gtob-error-notification' })

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  retry?: boolean
}

export default class APIClient {
  private requiresToken

  private instance

  constructor(requiresToken = true, headers = {}, baseURL = process.env.REACT_APP_API_URL) {
    this.requiresToken = requiresToken

    this.instance = axios.create({
      baseURL,
      headers: { ...headers },
    })

    this.instance.interceptors.request.use(async (config) => this.addTokenRequest(config))

    this.instance.interceptors.response.use(
      (response) => {
        const typedResponse = {
          ...response,
          data: APIClient.dataParser(response.data, response.status),
        }
        return typedResponse
      },
      async (error: AxiosError) => this.handleResponseError(error)
    )
  }

  private async addTokenRequest(config: InternalAxiosRequestConfig) {
    if (this.requiresToken) {
      if (!Token.hasRequiredScopes()) {
        Token.clearAndLogout()
        const abortController = new AbortController()
        abortController.abort()
        return { ...config, signal: abortController.signal }
      }

      if (Token.isValid(Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER)) {
        const tokenValue = await Token.getValue()
        if (tokenValue) {
          config.headers.Authorization = `Bearer ${tokenValue}`

          return config
        }
      } else {
        const abortController = new AbortController()
        Token.clearAndLogout()
        abortController.abort()
        const cfg = {
          ...config,
          signal: abortController.signal,
        }

        return cfg
      }
    }

    return config
  }

  private async handleResponseError(error: AxiosError) {
    const originalRequest = error.config as CustomAxiosRequestConfig

    if (this.requiresToken && error.response?.status === 401 && originalRequest) {
      if (originalRequest.retry || !Token.hasRequiredScopes()) {
        Token.clearAndLogout()
        message.error({
          content: <ErrorNotification error={error} />,
        })
        return Promise.reject(error)
      }

      originalRequest.retry = true

      if (!Token.isValid(Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER)) {
        Token.clearAndLogout()
        message.error({
          content: <ErrorNotification error={error} />,
        })
        return Promise.reject(error)
      }

      const newAccessToken = await Token.silentRefresh()

      if (newAccessToken) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return this.instance(originalRequest)
      }

      Token.clearAndLogout()
    }

    if (error.response?.status !== 404) {
      message.error({
        content: <ErrorNotification error={error} />,
      })
    }
    return Promise.reject(error)
  }

  private static dataParser(data: string | object, status?: number) {
    if (status === 204) {
      // 204 No Content
      return data
    }
    if (typeof data === 'string' && data.trim() === '') {
      return data
    }
    return typeof data === 'string' ? JSON.parse(data) : data
  }

  getInstance() {
    return this.instance
  }
}
